import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkle, FileArrowUp, FileText } from "@phosphor-icons/react";

export default function ContractFisaDialog({ projectId, onExtracted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [contracts, setContracts] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/projects/${projectId}/folder`)
      .then((r) => setContracts((r.data || []).filter((f) => f.category === "contract")))
      .catch(() => setContracts([]));
  }, [open, projectId]);

  const finish = (data) => {
    toast.success("Fișa a fost pre-completată din contract — verifică și corectează.");
    setOpen(false);
    onExtracted && onExtracted(data.fields || {});
  };

  const fromExisting = async (fid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/contract-extract-existing`, { file_id: fid });
      finish(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${projectId}/contract-extract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      finish(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="contract-fisa-btn"><Sparkle size={16} className="mr-1.5" /> Generează fișă din contract</Button>
      </DialogTrigger>
      <DialogContent onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Generează fișa din contract (AI)</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          {contracts.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">Există deja contract(e) în dosarul digital. Alege unul pentru a genera automat fișa proiectului:</p>
              <div className="grid gap-2">
                {contracts.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid={`contract-existing-${c.id}`}>
                    <FileText size={16} className="shrink-0 text-blue-400" />
                    <span className="min-w-0 flex-1 truncate text-sm">{c.name}</span>
                    <Button size="sm" onClick={() => fromExisting(c.id)} disabled={busy} data-testid={`contract-generate-${c.id}`}>
                      <Sparkle size={14} className="mr-1" />{busy ? "Se generează…" : "Generează fișa"}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="my-1 flex items-center gap-2 text-[11px] uppercase text-muted-foreground"><span className="h-px flex-1 bg-border" /> sau încarcă alt contract <span className="h-px flex-1 bg-border" /></div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Nu există încă un contract în dosar. Încarcă contractul (PDF sau Word) — se salvează în dosarul digital, iar AI-ul pre-completează automat fișa. Vei putea corecta orice câmp înainte de salvare.</p>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={onPick} data-testid="contract-file-input" />
          <Button variant={contracts.length > 0 ? "outline" : "default"} onClick={() => fileRef.current?.click()} disabled={busy} data-testid="contract-upload-btn">
            <FileArrowUp size={16} className="mr-1.5" />{busy ? "Se analizează…" : (contracts.length > 0 ? "Încarcă alt contract și generează" : "Alege contractul și generează fișa")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
